import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { meetingsLimitOptions } from '@app/account/teacher-page/user-service-form/user-service-form.variables';
import { AbstractContentPane } from '@app/library/panes/models/abstract-content-pane';
import { UtilsService } from '@core/services';
import { LibraryRouter } from '@models/library.router';
import { CLibraryFilterData } from '@store/models';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { EContentOrder, IContentPane } from 'lingo2-models';
import { filter } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface ILibrarySorting {
  show: boolean;
  pane?: IContentPane;
}

interface ISortOrder {
  value: EContentOrder;
  title: string;
}

const sortOrders: ISortOrder[] = [
  {
    value: EContentOrder.date_desc,
    title: 'library.content-filter.order.date_desc',
  },
  {
    value: EContentOrder.date_asc,
    title: 'library.content-filter.order.date_asc',
  },
  {
    value: EContentOrder.popularity,
    title: 'library.content-filter.order.popularity',
  },
  {
    value: EContentOrder.popularity_week,
    title: 'library.content-filter.order.popularity_week',
  },
  {
    value: EContentOrder.popularity_year,
    title: 'library.content-filter.order.popularity_year',
  },
];

@Component({
  selector: 'app-library-sorting',
  templateUrl: './library-sorting.component.html',
  styleUrls: ['./library-sorting.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibrarySortingComponent extends AbstractContentPane implements OnInit {
  private fb = inject(UntypedFormBuilder);
  protected utilsService = inject(UtilsService);
  protected router = inject(Router);

  @Input() public set pane(data: IContentPane) {
    if (!data) {
      return;
    }
    this.show = data.show_sorting;
    this._pane = data;
  }

  @Output() change = new EventEmitter<any>();

  public form: UntypedFormGroup;
  public show = false;
  public _pane: IContentPane;
  public sortingOptions = sortOrders;

  private _filterData: CLibraryFilterData;

  ngOnInit() {
    this.form = this.fb.group({
      order: [EContentOrder.popularity],
    });
    this.form.patchValue({ order: this.getOrder(this._pane) });
    this.store
      .select(getLibraryRouteData)
      .pipe(filter((libraryFilterData) => !!libraryFilterData))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (libraryFilterData) => {
          this._filterData = libraryFilterData;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (value) => {
        this.applyFilter(value.order);
        this.loadPane();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  getOrder(pane: IContentPane) {
    if (pane.contentFilter) {
      return pane.contentFilter.order;
    }
    if (pane.classroomsFilter) {
      return EContentOrder.popularity;
    }
    if (pane.userServiceFilter) {
      return pane.userServiceFilter.order;
    }
    if (pane.collectionsFilter) {
      return EContentOrder.popularity;
    }
    if (pane.meetingFilter && 'order' in pane.meetingFilter) {
      return pane.meetingFilter.order;
    }
    if (pane.userFilter) {
      return pane.userFilter.order;
    }
    if (pane.schoolsFilter) {
      return pane.schoolsFilter.order;
    }
    if (pane.directoriesFilter) {
      return pane.directoriesFilter.order;
    }
    return EContentOrder.popularity;
  }

  applyFilter(order: EContentOrder) {
    const libRouter = new LibraryRouter();
    libRouter.setBaseRoute(this._filterData.baseRoute);
    libRouter.setContentMode(this._filterData.contentMode);
    libRouter.setSubjects(this._filterData.subjectCodes);
    libRouter.setContentTypes(this._filterData.contentTypeCodes);
    libRouter.setOrder(order);
    libRouter.setLanguageCodes(this._filterData.languageCodes);

    const link = libRouter.getLink();
    this.router.navigate([link.path], { queryParams: link.parameters }).then();
  }

  protected readonly meetingsLimitOptions = meetingsLimitOptions;

  protected onContentLoaded(viaQuery?: boolean): void {}
}
